import React, {useEffect, useState} from 'react';
import Layout from '../components/Layout';
import {api} from '../../api';
import {Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';

const SHORT_DOMAIN = 'https://dr4.ca';

export default function ShortLinks() {
  const [links, setLinks] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [url, setUrl] = useState('');
  const [slug, setSlug] = useState('');
  const [label, setLabel] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [days, setDays] = useState(30);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [copied, setCopied] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editUrl, setEditUrl] = useState('');
  const [editSlug, setEditSlug] = useState('');
  const [editLabel, setEditLabel] = useState('');

  const [createError, setCreateError] = useState('');

  const load = () => api('/analytics/short-links').then(r => r.ok ? r.json() : []).then(setLinks);
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    api(`/analytics/short-links/${selected}?days=${days}`).then(r => r.ok ? r.json() : null).then(setDetail);
  }, [selected, days]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError('');
    const body = { destinationUrl: url.trim() };
    if (slug.trim()) body.slug = slug.trim();
    if (label.trim()) body.label = label.trim();
    const res = await api('/analytics/short-links', { method: 'POST', body: JSON.stringify(body) });
    if (res.ok) { setShowCreate(false); setUrl(''); setSlug(''); setLabel(''); setCreateError(''); load(); }
    else { const data = await res.json().catch(() => null); setCreateError(data?.error || 'Failed to create link'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await api(`/analytics/short-links/${deleteTarget}`, { method: 'DELETE' });
    setDeleteTarget(null);
    if (selected === deleteTarget) { setSelected(null); setDetail(null); }
    load();
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(`${SHORT_DOMAIN}/${link.slug}`);
    setCopied(link.id);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <Layout>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Short Links</h2>
        <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700">
          Create Link
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto mb-6">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Short URL</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Destination</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Label</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clicks</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {links.map(link => (
              <tr key={link.id} className={`hover:bg-gray-50 ${selected === link.id ? 'bg-amber-50' : ''}`}>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <a href={`${SHORT_DOMAIN}/${link.slug}`} target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:text-amber-800 font-medium text-sm">{SHORT_DOMAIN}/{link.slug}</a>
                    <button onClick={e => { e.stopPropagation(); copyLink(link); }}
                      className="text-gray-400 hover:text-amber-600">
                      {copied === link.id ? (
                        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                      )}
                    </button>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-600 text-sm truncate max-w-xs hidden sm:table-cell">{link.destinationUrl}</td>
                <td className="px-6 py-4 text-gray-600 text-sm hidden sm:table-cell">{link.label || '—'}</td>
                <td className="px-6 py-4 text-gray-900 font-medium">{link.clickCount || 0}</td>
                <td className="px-6 py-4 text-right">
                  <button onClick={() => setSelected(selected === link.id ? null : link.id)}
                    className="text-sm text-amber-600 hover:text-amber-800 mr-3">Analytics</button>
                  <button onClick={e => { e.stopPropagation(); setEditTarget(link); setEditUrl(link.destinationUrl); setEditSlug(link.slug); setEditLabel(link.label || ''); }}
                    className="text-sm text-amber-600 hover:text-amber-800 mr-3">Edit</button>
                  <button onClick={e => { e.stopPropagation(); setDeleteTarget(link.id); }}
                    className="text-sm text-red-600 hover:text-red-800">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {links.length === 0 && <div className="text-center py-12 text-gray-500">No short links yet. Create one to get started.</div>}
      </div>

      {selected && detail && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{detail.label || detail.slug} — Stats</h3>
            <select value={days} onChange={e => setDays(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Total Clicks</p>
              <p className="text-2xl font-bold text-gray-900">{detail.clickCount || 0}</p>
            </div>
          </div>

          {detail.clicksByDay && detail.clicksByDay.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-500 mb-3">Clicks Over Time</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={detail.clicksByDay}>
                  <XAxis dataKey="name" tick={{fontSize: 12}} />
                  <YAxis allowDecimals={false} tick={{fontSize: 12}} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#d97706" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTable title="Referrers" items={detail.topReferrers} />
            <StatTable title="Countries" items={detail.topCountries} />
            <StatTable title="Browsers" items={detail.topBrowsers} />
            <StatTable title="Devices" items={detail.topDevices} />
          </div>

          {detail.recentClicks && detail.recentClicks.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-500 mb-3">Recent Clicks</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Browser</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">OS</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Device</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Referrer</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {detail.recentClicks.map(click => (
                      <tr key={click.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{new Date(click.createdAt).toLocaleString()}</td>
                        <td className="px-4 py-2 text-gray-600 font-mono text-xs">{click.ipAddress || '—'}</td>
                        <td className="px-4 py-2 text-gray-600">{[click.city, click.country].filter(Boolean).join(', ') || '—'}</td>
                        <td className="px-4 py-2 text-gray-600">{click.browser || '—'}</td>
                        <td className="px-4 py-2 text-gray-600">{click.os || '—'}</td>
                        <td className="px-4 py-2 text-gray-600">{click.deviceType || '—'}</td>
                        <td className="px-4 py-2 text-gray-600 truncate max-w-xs">{click.referrer || '(direct)'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <form onSubmit={handleCreate} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Short Link</h3>
            {createError && <p className="text-sm text-red-600 mb-3">{createError}</p>}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Destination URL *</label>
                <input autoFocus type="url" required value={url} onChange={e => setUrl(e.target.value)}
                  placeholder="https://example.com/long-url" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Custom Slug (optional)</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">{SHORT_DOMAIN}/</span>
                  <input type="text" value={slug} onChange={e => setSlug(e.target.value)}
                    placeholder="my-link" className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label (optional)</label>
                <input type="text" value={label} onChange={e => setLabel(e.target.value)}
                  placeholder="Summer campaign" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={!url.trim()} className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">Create</button>
            </div>
          </form>
        </div>
      )}

      {editTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditTarget(null)}>
          <form onSubmit={async e => {
            e.preventDefault();
            const body = { destinationUrl: editUrl.trim(), slug: editSlug.trim(), label: editLabel.trim() || null };
            const res = await api(`/analytics/short-links/${editTarget.id}`, { method: 'PUT', body: JSON.stringify(body) });
            if (res.ok) { setEditTarget(null); load(); }
          }} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Short Link</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Destination URL</label>
                <input autoFocus type="url" required value={editUrl} onChange={e => setEditUrl(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">{SHORT_DOMAIN}/</span>
                  <input type="text" value={editSlug} onChange={e => setEditSlug(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                <input type="text" value={editLabel} onChange={e => setEditLabel(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setEditTarget(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={!editUrl.trim()} className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">Save</button>
            </div>
          </form>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeleteTarget(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Short Link</h3>
            <p className="text-gray-600 mb-6">Are you sure? This will permanently delete this link and all its click data.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function StatTable({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <h4 className="text-sm font-medium text-gray-500 mb-3">{title}</h4>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex justify-between text-sm">
            <span className="text-gray-700 truncate mr-4">{item.name || '(direct)'}</span>
            <span className="text-gray-500 font-medium flex-shrink-0">{item.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
